import { getReleaseGuardianEntitlement } from '@gonfalon/openapi/operations/getReleaseGuardianEntitlement';
import { internalGetAutomatedReleaseExemplarErrors } from '@gonfalon/openapi/operations/internalGetAutomatedReleaseExemplarErrors';
import { internalGetAutomatedReleaseMetricResults } from '@gonfalon/openapi/operations/internalGetAutomatedReleaseMetricResults';
import { internalGetAutomatedReleaseMetricTimeSeriesResults } from '@gonfalon/openapi/operations/internalGetAutomatedReleaseMetricTimeSeriesResults';
import { internalGetAutomatedReleaseRecommendedDuration } from '@gonfalon/openapi/operations/internalGetAutomatedReleaseRecommendedDuration';
import { internalGetBulkMetricDiagnostics } from '@gonfalon/openapi/operations/internalGetBulkMetricDiagnostics';
import { internalGetFlagDiagnostics } from '@gonfalon/openapi/operations/internalGetFlagDiagnostics';
import { internalGetMetricDiagnostic } from '@gonfalon/openapi/operations/internalGetMetricDiagnostic';
import { postProjectAutomatedRolloutConfig } from '@gonfalon/openapi/operations/postProjectAutomatedRolloutConfig';
import { getQueryClient } from '@gonfalon/react-query-client/getQueryClient';

import { createMutationHook } from '../internal/createMutationHook';
import { createQueryOptions } from '../internal/createQueryOptions';

export const getReleaseGuardianEntitlementsQuery = createQueryOptions(getReleaseGuardianEntitlement);

export const getFlagDiagnosticsQuery = createQueryOptions(internalGetFlagDiagnostics);
export const getMetricDiagnosticsQuery = createQueryOptions(internalGetMetricDiagnostic);
export const getBulkMetricDiagnosticsQuery = createQueryOptions(internalGetBulkMetricDiagnostics);
export const getRecommendedRolloutDurationQuery = createQueryOptions(internalGetAutomatedReleaseRecommendedDuration);
export const getExemplarErrorsQuery = createQueryOptions(internalGetAutomatedReleaseExemplarErrors);
export const getAutomatedReleaseMetricResultsQuery = createQueryOptions(internalGetAutomatedReleaseMetricResults);
export const getAutomatedReleaseMetricTimeSeriesResultsQuery = createQueryOptions(
  internalGetAutomatedReleaseMetricTimeSeriesResults,
);

import { getFlagReleaseOverview } from '@gonfalon/openapi/operations/getFlagReleaseOverview';
import { internalGetAutomatedReleaseLegacy } from '@gonfalon/openapi/operations/internalGetAutomatedReleaseLegacy';
import { internalGetAutomatedReleases } from '@gonfalon/openapi/operations/internalGetAutomatedReleases';
import { internalGetEnvironmentRelease } from '@gonfalon/openapi/operations/internalGetEnvironmentRelease';
import { internalGetEnvironmentReleaseRulesForFlag } from '@gonfalon/openapi/operations/internalGetEnvironmentReleaseRulesForFlag';
import { internalGetEnvironmentReleases } from '@gonfalon/openapi/operations/internalGetEnvironmentReleases';

export const getAutomatedReleaseQuery = createQueryOptions(internalGetAutomatedReleaseLegacy);

/** Release overview for a single flag: the latest rollout config's PR/deploy summary plus the latest environment release per configured environment, in config order. A flag with no rollout config returns 200 with empty `environments` and no `configId`. */
export const getFlagReleaseOverviewQuery = createQueryOptions(getFlagReleaseOverview);

/** Create an automated rollout config for a flag. Refreshes the flag's release overview so a newly created config shows up on the Overview page. */
export const createAutomatedRolloutConfigMutation = createMutationHook(postProjectAutomatedRolloutConfig, {
  onSuccess: async (_data, { projectKey, body }) => {
    const queryClient = getQueryClient();
    // Best-effort refresh. The config is already created, so a failed overview refetch must not reject
    // this callback — that would flip the mutation to its error path and report the create as failed.
    await queryClient
      .invalidateQueries({
        queryKey: getFlagReleaseOverviewQuery.partialQueryKey({ projectKey, flagKey: body.flagKey }),
      })
      .catch(() => {});
  },
});
export const getAutomatedReleasesQuery = createQueryOptions(internalGetAutomatedReleases);

/** Audience-phase release groupings for a flag: each group maps a release id to its ordered `phaseRuleIds` (oldest first). Used on the targeting page to group wire rules into a single release block. */
export const getAudiencePhaseReleasesForFlagQuery = createQueryOptions(internalGetEnvironmentReleaseRulesForFlag);

/** Environment releases for a flag, newest first, each with phases in audience order. Use to resolve which phase of a release a given rollout is on. */
export const getEnvironmentReleasesQuery = createQueryOptions(internalGetEnvironmentReleases);

/** One environment release with all phases' runtime state and events; pass `expand=metrics` to hydrate each guarded phase's metric metadata and configuration statuses. */
export const getEnvironmentReleaseQuery = createQueryOptions(internalGetEnvironmentRelease);
